import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useState } from 'react';
import { Box, Center, Row, Text } from '~/commonComponents';
import { getShouldShowChargeTimelineBanner } from '~/growthbook';
import { Icon } from '~/commonComponents/Icon/Icon';
import { useAppSelector } from '~/hooks';
import { Colors } from '~/themes/colors';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

export const ChargeTimelineCollapsibleBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const job = useAppSelector((state: any) => state.job.job);
  const chargeTimelineDays = RemoteConfigUtils.getChargeTimelineDays();
  const chargedHours = chargeTimelineDays * 24;

  // Check if feature flag is enabled
  const isFeatureEnabled = getShouldShowChargeTimelineBanner();

  if (!job || !isFeatureEnabled) {
    return null;
  }

  const { offer_owner: offerOwner, job_datetime: jobDateTime } = job;

  // Check if office is pre-approved (invoiced)
  const isPreApproved = offerOwner?.is_pre_approved;

  // Calculate hours until shift start
  const hoursUntilShift = moment(jobDateTime).diff(moment(), 'hours');

  // Only show if NOT pre-approved AND more than 168 hours (7 days) until shift
  if (isPreApproved || hoursUntilShift <= chargedHours) {
    return null;
  }

  // Calculate charge date (job start time - 168 hours / 7 days before shift)
  const chargeDate = moment(jobDateTime)
    .subtract(chargedHours, 'hours')
    .format('ddd, MMM DD, YYYY');

  return (
    <Box margin={'20px 0px'}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          all: 'unset',
          width: '100%',
          backgroundColor: Colors.success_30,
          border: `1px solid ${Colors.success_600}`,
          padding: '10px 12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          boxSizing: 'border-box',
        }}
      >
        {/* Top row: Icon + Title + Badge + Arrow */}
        <Row alignItems="center" justifyContent="space-between">
          <Row alignItems="center">
            <Icon name="solid-dollar" size={24} color={Colors.success_500} />
            <Text fontSize={18} bold color={Colors.success_500} marginLeft={4}>
              Book Now, Pay Later!
            </Text>
            <Center
              marginLeft={10}
              height={20}
              width={48}
              backgroundColor={Colors.secondary_500}
              borderRadius={3}
            >
              <span
                style={{
                  fontFamily: 'Nunito',
                  fontSize: 12,
                  fontWeight: 700,
                  color: Colors.white,
                  textTransform: 'uppercase',
                }}
              >
                NEW
              </span>
            </Center>
          </Row>
          <Center
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          >
            <Icon name="chevron-down" size={24} color={Colors.success_500} />
          </Center>
        </Row>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden',
              backgroundColor: Colors.success_30,
              border: `1px solid ${Colors.success_600}`,
              borderTopWidth: 0,
              padding: '0px 40px 12px 40px',
              marginTop: -1,
            }}
          >
            <Text color="success_500">Professional Pay: Charged on {chargeDate}</Text>
            <br />
            <Text color="success_500">Additional Fees: Charged upon confirmation</Text>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
